import { useVuelidate } from '@vuelidate/core';
import {
  computed,
  provide,
  reactive,
  ref,
  set,
  toRef,
} from 'vue';

import { noop } from '@/shared/lib';

import type { InjectionKey } from 'vue';

type FieldRules = Record<string, unknown>;

export interface FormValidationFieldNode {
  $model: unknown;
  $dirty: boolean;
  $error: boolean;
  $errors: unknown[];
  $invalid: boolean;
  $pending: boolean;
  $anyDirty: boolean;
  $touch: () => void;
  $reset: () => void;
  [validatorName: string]: unknown;
}

export interface ValidationInjection {
  readonly $v: Record<string, FormValidationFieldNode>;
  readonly serverErrors: Record<string, string>;
  readonly validators: { form: Record<string, FieldRules> };
  readonly form: Record<string, unknown>;
}

export const VALIDATION_INJECTION_KEY: InjectionKey<ValidationInjection> = Symbol('validation');

export interface UseFormValidatorOptions {
  initialForm?: Record<string, unknown>;
}

interface FormValidatorState {
  form: Record<string, unknown>;
  serverErrors: Record<string, string>;
  validators: { form: Record<string, FieldRules> };
}

function createFallbackFieldNode (
  state: FormValidatorState,
  key: string,
): FormValidationFieldNode {
  return {
    get $model () {
      return state.form[key];
    },
    set $model (value: unknown) {
      set(state.form, key, value);
    },
    $dirty: false,
    $error: false,
    $errors: [],
    $invalid: false,
    $pending: false,
    $anyDirty: false,
    $touch: noop,
    $reset: noop,
  };
}

export function useFormValidator (options: UseFormValidatorOptions = {}) {
  const state = reactive<FormValidatorState>({
    form: { ...options.initialForm },
    serverErrors: {},
    validators: { form: {} },
  });

  const rules = computed(() => ({ form: { ...state.validators.form } }));

  const v$ = useVuelidate(
    rules,
    { form: state.form },
    { $scope: false },
  );

  const formInvalid = computed<boolean>(() => {
    const invalid = v$.value.form?.$invalid ?? false;

    return !Object.keys(state.form).length || invalid;
  });

  const isSubmitting = ref(false);
  const isSubmitDisabled = computed<boolean>(
    () => formInvalid.value || isSubmitting.value,
  );

  function setServerError (field: string, message: string): void {
    set(state.serverErrors, field, message);
  }

  const fallbackCache = new Map<string, FormValidationFieldNode>();

  const vFormProxy = new Proxy({} as Record<string, FormValidationFieldNode>, {
    get (_target, key) {
      const node = v$.value.form as unknown as Record<PropertyKey, unknown>;

      if (typeof key !== 'string' || key in node || key.startsWith('$')) {
        return node[key];
      }

      let fallback = fallbackCache.get(key);
      if (!fallback) {
        fallback = createFallbackFieldNode(state, key);
        fallbackCache.set(key, fallback);
      }

      return fallback;
    },
  });

  const validation: ValidationInjection = {
    get $v () {
      return vFormProxy;
    },
    get serverErrors () {
      return state.serverErrors;
    },
    get validators () {
      return state.validators;
    },
    get form () {
      return state.form;
    },
  };

  provide(VALIDATION_INJECTION_KEY, validation);

  return {
    form: toRef(state, 'form'),
    serverErrors: toRef(state, 'serverErrors'),
    validators: toRef(state, 'validators'),
    v$,
    formInvalid,
    isSubmitting,
    isSubmitDisabled,
    setServerError,
  };
}
